'use client';

import { useState, useEffect, useCallback } from 'react';
import { authService } from '@/lib/authService';
import { createLogger } from '@/lib/debugLogger';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

const logger = createLogger('useLastFuelEntry');

// ============================================================================
// TYPES
// ============================================================================

export interface LastFuelEntry {
  id: string;
  fillDate: string;
  odometerReading: number;
  litres: number;
  costPerLitre: number;
  totalCost: number;
  fuelType: string;
  stationName: string;
  source: 'smarttask' | 'fleet';
}

export interface UseLastFuelEntryParams {
  /** Only fetch when true (typically selectedEntryType === 'fuel' && effectiveVehicleId) */
  enabled: boolean;
  /** Vehicle UUID or external ID */
  vehicleId: string | null | undefined;
}

export interface UseLastFuelEntryReturn {
  lastFuelEntry: LastFuelEntry | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

export function useLastFuelEntry({
  enabled,
  vehicleId,
}: UseLastFuelEntryParams): UseLastFuelEntryReturn {
  const [lastFuelEntry, setLastFuelEntry] = useState<LastFuelEntry | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !vehicleId) {
      setLastFuelEntry(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const token = authService.getTokens()?.access_token;
        const params = new URLSearchParams({
          action: 'getFuelEntries',
          vehicleId,
        });
        const resp = await fetch(`/api/v1/work-items/fleet?${params}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!resp.ok) {
          if (!cancelled) {
            setError('Failed to load previous fuel data');
            setLastFuelEntry(null);
          }
          return;
        }

        const data = await resp.json();
        if (!cancelled) {
          const entries = Array.isArray(data?.data) ? data.data : [];
          if (entries.length === 0) {
            setLastFuelEntry(null);
            setError(null);
            return;
          }

          // The API returns entries ordered by created_at desc, so the first
          // entry is the most recent. Parse it into our normalized shape.
          const raw = entries[0];
          const entry: LastFuelEntry = {
            id: String(raw.id || ''),
            fillDate: String(raw.fill_date || raw.created_at || ''),
            odometerReading: Number(raw.odometer_reading || 0),
            litres: Number(raw.liters || 0),
            costPerLitre: Number(raw.cost_per_liter || 0),
            totalCost: Number(raw.total_cost || 0),
            fuelType: String(raw.fuel_type || ''),
            stationName: String(raw.station_name || ''),
            source: raw.source === 'smarttask' ? 'smarttask' : 'fleet',
          };

          // Only use the entry if it has a meaningful odometer reading
          if (entry.odometerReading > 0) {
            setLastFuelEntry(entry);
          } else {
            setLastFuelEntry(null);
          }
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          if (!isSilentFetchError(err)) {
            logger.warn('[useLastFuelEntry] Failed to fetch last fuel entry:', err);
          }
          setError(null);
          setLastFuelEntry(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [enabled, vehicleId, refetchTrigger]);

  return { lastFuelEntry, loading, error, refetch };
}
